import type { Addon } from '@/shared/lib/db/schemas/cart.schema';

/**
 * Obtém o tipo de cálculo de preço com fallback para compatibilidade
 * 
 * @param addon - Addon com informações da categoria
 * @returns 0 = soma normal, 1 = preço maior, 2 = preço médio
 */
function getPriceCalculationType(addon: Addon): number {
  return addon.price_calculation_type ?? 
    (addon.consider_most_expensive === 1 ? 1 : 0);
}

/**
 * Calcula o preço de uma categoria específica baseado no tipo de cálculo
 * 
 * @param categoryAddons - Addons da categoria
 * @param calcType - Tipo de cálculo (0, 1 ou 2)
 * @returns Preço calculado da categoria
 */
function calculateCategoryPrice(categoryAddons: Addon[], calcType: number): number {
  if (categoryAddons.length === 0) {
    return 0;
  }

  const prices = categoryAddons.map(addon => addon.price * addon.quantity);

  switch (calcType) {
    case 1: {
      // Preço maior: cobra apenas o adicional de maior valor UNITÁRIO,
      // independente da quantidade selecionada.
      // Ex.: pizza meio a meio com 2x Abacaxi (R$87,90) + 1x Bacon (R$134,53)
      // cobra o sabor mais caro (R$134,53), e não 87,90×2 = R$175,80.
      const unitPrices = categoryAddons.map(addon => addon.price);
      return Math.max(...unitPrices);
    }

    case 2: {
      // Média PONDERADA: soma ponderada ÷ total de PARTES
      // Exemplo pizza 4 sabores: 2x Abacaxi (R$87,90) + 1x Bacon (R$134,53) + 1x Atum (R$31,97)
      // Total partes = 2 + 1 + 1 = 4
      // Soma ponderada = (87,90×2) + (134,53×1) + (31,97×1) = 342,30
      // Média = 342,30 / 4 = R$ 85,57
      const totalParts = categoryAddons.reduce((sum, addon) => sum + addon.quantity, 0);
      const weightedSum = prices.reduce((a, b) => a + b, 0);
      return weightedSum / totalParts;
    }
    
    default:
      // Soma normal (calcType === 0)
      return prices.reduce((a, b) => a + b, 0);
  }
}

/**
 * Calcula o preço total dos addons considerando o campo price_calculation_type
 * 
 * Tipos de cálculo:
 * - 0: Soma normal (soma todos os valores)
 * - 1: Preço maior (considera apenas o mais caro)
 * - 2: Preço médio (média dos valores selecionados)
 * 
 * Fallback para consider_most_expensive se price_calculation_type não existir.
 */
export function calculateAddonsPrice(addons: Addon[]): number {
  if (!Array.isArray(addons) || addons.length === 0) {
    return 0;
  }

  // Agrupa os addons por categoria
  const addonsByCategory = new Map<string, Addon[]>();

  addons.forEach(addon => {
    const categoryId = addon.addon_category_id;
    if (!addonsByCategory.has(categoryId)) {
      addonsByCategory.set(categoryId, []);
    }
    addonsByCategory.get(categoryId)!.push(addon);
  });

  // Calcula o preço total considerando price_calculation_type
  let totalPrice = 0;

  addonsByCategory.forEach(categoryAddons => {
    // Usa o primeiro addon da categoria para determinar o tipo de cálculo
    const calcType = getPriceCalculationType(categoryAddons[0]);
    totalPrice += calculateCategoryPrice(categoryAddons, calcType);
  });

  return totalPrice;
}
